import { SubmitOrderParams } from 'components/ordering/SubmitOrderParams'
import * as uuid from 'uuid'
import { isEmpty } from 'lodash'
import {
  DeliveryQuote,
  Locale,
  MenuCategory,
  MenuItem,
  MenuModifier,
  MenuOption,
  Order,
  OrderItem,
  OrderModifier,
  OrderOption,
  OrderType,
  Place,
  Store,
  StoreBizHour,
  StoreHoliday,
  SubmitOrderResponse,
  defaultDelayTime,
  defaultDeliveryTime,
  defaultLocale,
  defaultTZ,
  displayFullDateMonthFormat,
  displayHourFormat,
  timestampFormat,
} from 'models'
import moment from 'moment-timezone'
import {
  currentTzTime,
  formatTime,
  isInBizHour,
  isInWorkingHoursHoliday,
  isToday,
  parseTzTime,
  toTzTime,
  isStoreOpening,
} from './TimeService'

export const newId = (): string => {
  return moment().format(timestampFormat)
}

export const roundM = (amount: number): number => {
  return Math.round(amount * 100) / 100
}

export const formatCurrencyWithLocale = (
  amount: number,
  locale: Locale,
): string => {
  return new Intl.NumberFormat(locale.locale, {
    style: 'currency',
    currency: locale.currency,
  }).format(amount)
}

export const newOrder = (
  store: Store,
  orderType: OrderType,
  tableId?: string,
  isAsap: boolean = true,
): Order => {
  const tipSettings = store.tip_settings
  let tipPercent = 0
  if (
    tipSettings?.enable_tip_guides === true &&
    tipSettings?.tip_guides !== undefined &&
    tipSettings.default_tip_guide !== undefined
  ) {
    tipPercent = tipSettings.tip_guides[tipSettings.default_tip_guide]
  }
  const timeAsId = moment()
    .tz(store.timezone ?? defaultTZ)
    .format(timestampFormat)
  const order: Order = {
    id: timeAsId,
    time: tableId === undefined ? timeAsId : undefined,
    unique_id: uuid.v4(),
    name: '',
    type: 'online-order',
    items: [],
    tip: 0,
    tax_amount: 0,
    subtotal: 0,
    total: 0,
    order_type: orderType,
    customer_name: '',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_address: '',
    customer_phone: '',
    customer_zip: '',
    customer_state: 'CA',
    customer_city: '',
    customer_place_unit: '',
    card: {
      name: '',
      cvc: '',
      number: '',
      expiry: '',
    },
    billing_address: '',
    billing_phone: '',
    storeid: store.id,
    tax: { ...(store.tax as any) },
    no_plastic_utensils:
      tableId !== undefined
        ? false
        : store.footnotes_settings?.no_plastic_utensils !== false,
    contactless_delivery:
      tableId !== undefined
        ? false
        : store.footnotes_settings?.no_contactless_delivery === false,
    payment_method: 'credit_card',
    is_asap: isAsap,
    tip_percent: tipPercent,
    table: tableId,
  }

  return order
}

export const getOrderItemCount = (order: Order): number => {
  return order.items?.reduce((acc, item) => acc + (item.count ?? 0), 0) ?? 0
}

export const newOrderItem = (
  mCategory: MenuCategory,
  mItem: MenuItem,
): OrderItem => {
  return {
    id: newId(),
    type: 'order-item',
    categoryid: mCategory.id,
    itemid: mItem.id,
    name: mItem.name || '',
    name2: mItem.name2,
    price: mItem.price,
    count: 1,
    image_url: mItem.imageURL,
    note: '',
    modifiers: [],
    subtotal: 0,
    non_taxable: mItem.non_taxable,
    gift_card: mCategory.gift_card,
    gift_certificate: mCategory.gift_certificate,
    item_note: mItem.note,
  }
}

export const newOrderModifier = (
  mModifier: MenuModifier,
  mOption: MenuOption,
): OrderModifier => {
  return {
    id: mModifier.id,
    type: 'order-modifier',
    name: mModifier.name ?? '',
    prefix: mModifier.prefix ?? '',
    sameline: mModifier.sameline ?? false,
    options: [newOrderOption(mOption)],
    global: mModifier.global ?? false,
  }
}

export const newOrderOption = (mOption: MenuOption): OrderOption => {
  return {
    id: mOption.id,
    type: 'order-option',
    name: mOption.name || '',
    name2: mOption.name2 || '',
    price: mOption.price || 0,
    subtotal: 0,
    count: 1,
    suboptions: [],
    required_level: mOption.required_level || '',
  }
}

export const loadMenuItemByOrderItem = (
  orderItem: OrderItem,
  categories: MenuCategory[],
): MenuItem | undefined => {
  const category = categories.find((c) => c.id === orderItem.categoryid)
  return category?.items?.find((i) => i.id === orderItem.itemid)
}

export const loadMenuModifiersByOrderItem = (
  orderItem: OrderItem,
  categories: MenuCategory[],
  modifiers: MenuModifier[],
): MenuModifier[] => {
  // Find the matching category
  const category = categories.find((c) => c.id === orderItem.categoryid)
  if (!category || !category.items) {
    return []
  }
  // Find the matching item
  const item = category.items.find((i) => i.id === orderItem.itemid)
  if (!item || !item.modifiers) {
    return []
  }

  // Fetch modifiers detail from list of all modifiers
  const orderItemModifiers: (MenuModifier | undefined)[] = item.modifiers.map(
    (itemModifier) => {
      // Find modifier from list of all modifiers
      const modifier = modifiers.find(
        (m) =>
          m.id === itemModifier.id &&
          (!m.online_ordering || m.online_ordering === true),
      )
      if (!modifier) {
        return undefined
      }

      let itemModOptions = itemModifier.options || []
      // Default to use the full list of options
      let options = modifier.options || []
      // But if there is limitation on the list of options
      // then we must only take the available options
      if (itemModOptions) {
        options = options.filter((opt) => itemModOptions.includes(opt.id))
      }

      return {
        id: itemModifier.id,
        name: modifier.name,
        prefix: modifier.prefix,
        selection_level: itemModifier.selection_level,
        options,
        global: itemModifier.global,
      } as MenuModifier
    },
  )

  // Filter out the not found modifier or modifier with empty options
  // this can happen if there is inconsitent data in which item contains modifier
  // that was somehow deleted
  const noEmptyModfiers: MenuModifier[] = orderItemModifiers.filter(
    (modifier) => modifier !== undefined,
  ) as MenuModifier[]
  return noEmptyModfiers.sort(compareBySelectionLevel)
}

const compareBySelectionLevel = (
  m1: MenuModifier,
  m2: MenuModifier,
): number => {
  let m1V = 0
  let m2V = 0
  if (m1.selection_level && isRequiredSelectionLevel(m1.selection_level)) {
    m1V = 1
  }
  if (m2.selection_level && isRequiredSelectionLevel(m2.selection_level)) {
    m2V = 1
  }
  return m2V - m1V
}

export const formatSelectionLevel = (sl?: string): string => {
  switch (sl) {
    case 'one-or-more':
      return 'one or more options'
    case 'two-or-more':
      return 'two or more options'
    case 'exactly-1':
      return 'exactly one option'
    case 'exactly-2':
      return 'exactly two options'
    case 'exactly-3':
      return 'exactly three options'
    case '0-3-options':
      return 'max three options'
    default:
      return ''
  }
}

export const isRequiredSelectionLevel = (sl?: string): boolean => {
  return requiredOptionCountForSelectionLevel(sl) > 0
}

export const maxOptionCountForSelectionLevel = (sl?: string): number => {
  switch (sl) {
    case 'exactly-1':
      return 1
    case 'exactly-2':
      return 2
    case 'exactly-3':
      return 3
    case '0-3-options':
      return 3
    default:
      return 0
  }
}

const requiredOptionCountForSelectionLevel = (sl?: string): number => {
  switch (sl) {
    case 'one-or-more':
      return 1
    case 'two-or-more':
      return 2
    case 'exactly-1':
      return 1
    case 'exactly-2':
      return 2
    case 'exactly-3':
      return 3
    default:
      return 0
  }
}

export const updateOrderOptionSubtotal = (
  oOption: OrderOption,
  count: number,
  orderType: OrderType,
  store: Store,
): OrderOption => {
  // Store original price if not already stored
  const updatedOption = { ...oOption }
  if (updatedOption.original_price === undefined) {
    updatedOption.original_price = updatedOption.price ?? 0
  }

  // Adjust the price
  const adjustedPrice = getAdjustedOrderItemPrice(
    updatedOption.original_price,
    orderType,
    store,
  )

  let optionsSubtotal = 0
  updatedOption.suboptions?.forEach((oos) => {
    // Store original price if not already stored
    if (oos.original_price === undefined) {
      oos.original_price = oos.price ?? 0
    }
    // Adjust the price
    oos.price = getAdjustedOrderItemPrice(oos.original_price, orderType, store)
    optionsSubtotal += oos.price
  })

  // Update subtotal
  const subtotal = roundM((optionsSubtotal + adjustedPrice) * count)

  return {
    ...updatedOption,
    price: adjustedPrice,
    subtotal,
  }
}

export const updateOrderModifierSubtotal = (
  oModifier: OrderModifier,
  count: number,
  orderType: OrderType,
  store: Store,
): OrderModifier => {
  // Update all options in the modifier
  const options = oModifier.options.map((oOption) =>
    updateOrderOptionSubtotal(oOption, count, orderType, store),
  )

  // Calculate modifier subtotal
  const subtotal = options.reduce(
    (sum, oOption) => sum + (oOption.subtotal ?? 0),
    0,
  )

  return {
    ...oModifier,
    options,
    subtotal,
  }
}

export const updateOrderItemSubtotal = (
  oItem: OrderItem,
  orderType: OrderType,
  store: Store,
): OrderItem => {
  const count = oItem.count ?? 0

  // Store original prices if not already stored
  if (oItem.original_price === undefined) {
    oItem.original_price = oItem.price ?? 0
  }

  // Update all modifiers in the item
  const oModifiers = oItem.modifiers.map((oModifier) =>
    updateOrderModifierSubtotal(oModifier, count, orderType, store),
  )

  // Calculate item subtotal
  const modifiersSubtotal = oModifiers.reduce(
    (sum, oModifier) => sum + (oModifier.subtotal ?? 0),
    0,
  )

  // Apply delivery surcharge if needed
  let adjustedItemPrice = getAdjustedOrderItemPrice(
    oItem.original_price,
    orderType,
    store,
  )

  // Calculate total subtotal
  const subtotal = roundM(
    modifiersSubtotal +
      adjustedItemPrice * count +
      (oItem.price_note ?? 0) * count,
  )

  return {
    ...oItem,
    modifiers: oModifiers,
    subtotal,
    price: adjustedItemPrice,
    price_note: oItem.price_note ?? 0,
    promotion_price: oItem.IsPromotion === true ? -adjustedItemPrice : 0,
  }
}

export const updateOrderAmounts = (order: Order, store: Store): Order => {
  const oItems = order.items.map((oItem) =>
    updateOrderItemSubtotal(oItem, order.order_type, store),
  )
  const { subtotal, quantity, nonTaxableTotal } = oItems.reduce(
    (acc, oItem) => {
      acc.subtotal += oItem.subtotal ?? 0 // TODO: revisit promotion price
      acc.quantity += oItem.count ?? 0
      acc.nonTaxableTotal +=
        oItem.non_taxable || oItem.gift_card || oItem.gift_certificate
          ? (oItem.subtotal ?? 0)
          : 0
      return acc
    },
    {
      subtotal: 0,
      quantity: 0,
      nonTaxableTotal: 0,
    },
  )

  const deliveryFeeAmount = order.delivery_fee_amount ?? 0

  let discountAmount = 0
  if (order.discount) {
    discountAmount = roundM(subtotal * order.discount.percent)
  }

  const customFee = store.custom_fee_settings
  let customFeeAmount = 0
  if (customFee && customFee.amount && customFee.amount > 0 && subtotal > 0) {
    customFeeAmount = customFee.amount
  }

  const customFeePercent = store.custom_fee_settings?.custom_fee_percent
  let customFeePercentAmount = 0
  if (
    customFeePercent &&
    customFeePercent.percent &&
    customFeePercent.percent > 0 &&
    subtotal > 0
  ) {
    customFeePercentAmount = roundM(subtotal * customFeePercent.percent)
  }

  // The tax amount will now include the custom fee in its calculation.
  let taxAmount = 0
  //if (getApplyTax(store)) {// TODO: revisit when doing kiosk
  if (order.tax && order.tax.percent) {
    const taxableAmount =
      subtotal +
      deliveryFeeAmount +
      customFeeAmount +
      customFeePercentAmount -
      discountAmount -
      nonTaxableTotal

    if (taxableAmount > 0) {
      taxAmount = roundM(taxableAmount * order.tax.percent)
    }
  }
  //}

  let tipAmount = order.tip ?? 0
  if (
    store.tip_settings?.enable_tip_guides === true &&
    order.tip_percent !== undefined &&
    (order.table === undefined ||
      store.table_ordering_settings?.show_tip === true)
  ) {
    tipAmount = roundM(subtotal * order.tip_percent)
  }

  let defaultTipForDriver = order?.default_tip_for_driver
  let tipForDriver = undefined
  let tipForStaff = undefined
  if (checkIsSplitTip(store, order.order_type)) {
    defaultTipForDriver =
      order?.default_tip_for_driver ??
      store?.tip_settings?.default_tip_for_driver
    tipForDriver = roundM(tipAmount * (defaultTipForDriver! / 100))
    tipForStaff = tipAmount - tipForDriver
  }

  const total =
    subtotal +
    deliveryFeeAmount -
    discountAmount +
    taxAmount +
    tipAmount +
    customFeeAmount +
    customFeePercentAmount

  return {
    ...order,
    items: oItems,
    quantity,
    subtotal,
    discount_amount: discountAmount,
    tax_amount: taxAmount,
    custom_fee: customFeeAmount > 0 ? customFee : undefined,
    custom_fee_percent:
      customFeePercentAmount > 0 ? customFeePercent : undefined,
    total,
    tip: tipAmount,
    default_tip_for_driver: defaultTipForDriver,
    tip_for_driver: tipForDriver,
    tip_for_restaurant_staff: tipForStaff,
  }
}

export const getOItemDisplayName = (
  oModifier: OrderModifier,
  oOption: OrderOption,
): string => {
  const parts = [
    oModifier.prefix ?? '',
    oOption.name ?? '',
    ...(oOption.suboptions?.map((subOption) => subOption.name ?? '') ?? []),
  ]
  return parts.join(' ')
}

export const isOrderModifersSatisfied = (
  oModifiers: OrderModifier[],
  mModifiers: MenuModifier[],
): boolean => {
  if (mModifiers.length === 0) {
    return true
  }

  for (const mModifier of mModifiers) {
    const minOptionCount = requiredOptionCountForSelectionLevel(
      mModifier.selection_level,
    )
    const oModifier = oModifiers.find((m) => m.id === mModifier.id)

    // Validate required sub options
    if (oModifier) {
      for (const option of oModifier.options) {
        if (
          option.required_level === 'exact_one' &&
          isEmpty(option.suboptions)
        ) {
          return false
        }
      }
    }

    // No required options
    if (minOptionCount === 0) continue

    // Not enough required options
    if (!oModifier || oModifier.options.length < minOptionCount) return false
  }

  return true
}

export const createSubmitParamsFromOrder = (
  order?: Order,
): SubmitOrderParams => ({
  orderInfo: {
    deliveryPlace: order?.customer_place,
    deliveryFee: order?.delivery_fee_amount,
  },
  customerInfo: {
    firstName: order?.customer_first_name ?? '',
    lastName: order?.customer_last_name ?? '',
    email: order?.customer_email ?? '',
    phone: order?.customer_phone ?? '',
  },
  paymentInfo: {
    paymentMethod: order?.payment_method ?? 'credit_card',
    cardNumber: order?.card?.number ?? '',
    cardName: order?.card?.name ?? '',
    cardExpiry: order?.card?.expiry ?? '',
    cardCode: order?.card?.cvc ?? '',
    billingPlace: order?.billing_place,
    billingAddress: order?.billing_address ?? '',
    billingCity: order?.billing_city ?? '',
    billingState: order?.billing_state ?? '',
    billingZip: order?.billing_zip ?? '',
  },
})

export const createAddressFromUnitAndPlace = (
  place: Place,
  unit: string,
): string => {
  const parts = [place.street, unit, place.city, place.state, place.zip_code]
  return parts.filter((p) => !isEmpty(p)).join(', ')
}

// This is called before submitting order providing a chance
// to update or correct values before submitting to the server.
export const updateOrderForSubmitting = (order: Order, store: Store): Order => {
  // Remove place_id if saved_address_id exists
  const customer_place = order.customer_place ?? {}

  const isStoreCatering = canSetOrderDate(store, order.order_type)
  const hasCateringItems = getCateringPreparationDays(order, store) > 0
  const isSameDay = isToday(toTzTime(order.timeIsoFormat, store.timezone))

  return {
    ...order,
    is_catering: (isStoreCatering || hasCateringItems) && !isSameDay,
    customer_place,
    customer_address: createAddressFromUnitAndPlace(
      customer_place as Place,
      order.customer_place_unit ?? '',
    ),
    customer_name:
      `${order.customer_first_name} ${order.customer_last_name}`.trim(),
    customer_phone: order.customer_phone?.replace(/\D/g, ''),
    card: {
      ...order.card,
      number: order.card?.number?.replace(/\D/g, ''),
      expiry: order.card?.expiry?.replace(/\D/g, ''),
      cvc: order.card?.cvc?.replace(/\D/g, ''),
    },
  }
}

export const getDelayTimeInMinutes = (
  store: Store,
  orderType?: OrderType,
): number => {
  if (orderType === 'delivery')
    return store.delay_delivery_time ?? defaultDelayTime
  if (orderType === 'pickup') return store.delay_pickup_time ?? defaultDelayTime
  if (orderType === 'dinein') return store.delay_dinein_time ?? defaultDelayTime
  return defaultDelayTime
}

export const getDeliveryTimeInMinutes = (
  store: Store,
  orderType?: OrderType,
  deliveryQuote?: DeliveryQuote,
): number => {
  if (orderType !== 'delivery') return 0
  if (deliveryQuote?.dropoff_eta) {
    return moment(deliveryQuote?.dropoff_eta).diff(moment(), 'minutes')
  }
  return store.delivery_settings?.delivery_time ?? defaultDeliveryTime
}

export const getTotalDelayTimeInMinutes = (
  store: Store,
  orderType?: OrderType,
  deliveryQuote?: DeliveryQuote,
): number => {
  return (
    getDelayTimeInMinutes(store, orderType) +
    getDeliveryTimeInMinutes(store, orderType, deliveryQuote)
  )
}

type Note = [string, any]

export const getOrderSubmittedNote = (
  store: Store,
  order: Order,
  res: SubmitOrderResponse,
): Note => {
  if (order.table !== undefined) {
    return [
      'We received your order at {{time}}.',
      { time: formatTime(order.time, store.timezone, displayHourFormat) },
    ]
  } else if (order.is_asap === true) {
    if (order.order_type === 'pickup') {
      return [
        `Your order is ready for pick up in {{minutes}} minutes from now.`,
        { minutes: store.delay_pickup_time ?? defaultDelayTime },
      ]
    } else if (order.order_type === 'dinein') {
      return [
        `Your order is ready for dine in {{minutes}} minutes from now.`,
        { minutes: store.delay_dinein_time ?? defaultDelayTime },
      ]
    } else if (order.order_type === 'delivery') {
      if (isEmpty(res.delivery_iso_dt)) {
        const duration =
          (store.delay_delivery_time ?? defaultDelayTime) +
          (store.delivery_settings?.delivery_time ?? 0)
        return [
          `Your order will be delivered approximately in {{minutes}} minutes from now.`,
          { minutes: duration },
        ]
      } else {
        return [
          `Your order will be delivered approximately at {{deliveryTime}}.`,
          {
            deliveryTime: formatTime(res.delivery_iso_dt, store?.timezone),
          },
        ]
      }
    }
  } else {
    const orderTime = toTzTime(order.timeIsoFormat, store.timezone)
    const isSameDay = isToday(orderTime, store.timezone)

    const readyDate = formatTime(
      order.timeIsoFormat,
      store.timezone,
      displayFullDateMonthFormat,
    )

    let readyTime = formatTime(order.timeIsoFormat, store.timezone)
    if (order.order_type === 'pickup') {
      if (!isSameDay) {
        return [
          `Your order is ready for pick up on {{readyDate}} at {{readyTime}}.`,
          { readyDate, readyTime },
        ]
      }

      return [
        'Your order is ready for pick up at {{readyTime}}.',
        { readyTime },
      ]
    } else if (order.order_type === 'dinein') {
      if (!isSameDay) {
        return [
          `Your order is ready for dine in on {{readyDate}} at {{readyTime}}.`,
          { readyDate, readyTime },
        ]
      }

      return [
        'Your order is ready for dine in at {{readyTime}}.',
        { readyTime },
      ]
    } else if (order.order_type === 'delivery') {
      if (!isEmpty(res.delivery_iso_dt)) {
        readyTime = formatTime(res.delivery_iso_dt, store.timezone)
      }

      if (!isSameDay) {
        return [
          `Your order will be delivered approximately on {{readyDate}} at {{readyTime}}.`,
          { readyDate, readyTime },
        ]
      }

      return [
        `Your order will be delivered approximately at {{readyTime}}.`,
        { readyTime },
      ]
    }
  }
  return ['', {}]
}

export const buildOrderTypeNotes = (
  store: Store,
  orderType: OrderType,
  cateringPreparationDays: number = 0,
): Note[] => {
  const deliverySettings = store.delivery_settings

  const preparationTime = store && getDelayTimeInMinutes(store, orderType)
  const storeAddress = deliverySettings?.delivery_from_address?.description
  const isDeliveredByOwner = deliverySettings?.delivery_type === 'owner'
  const byOwnerDeliveryTime = deliverySettings?.delivery_time ?? 20
  const byOwnerMaxDeliveryDistance =
    deliverySettings?.maximum_delivery_distance ?? 0
  const byOwnerMinAmountToAcceptDelivery =
    deliverySettings?.accept_delivery_amount ?? 0
  const byOwnerFreeDeliveryAmount = deliverySettings?.free_delivery_amount ?? 0

  const notes: Note[] = []
  notes.push([
    'We need at least <strong>{{preparationTime}} mins</strong> to prepare your order.',
    { preparationTime },
  ])
  if (cateringPreparationDays > 0) {
    const earliestOrderDate = calculateCateringEarliestOrderDate(
      cateringPreparationDays,
      store.timezone,
    ).format(displayFullDateMonthFormat)
    notes.push([
      'Your order contains catering items which require at least <strong>{{cateringPreparationDays}} day(s)</strong> of preparation. ASAP is not available and the earliest date you can select is <strong>{{earliestOrderDate}}</strong>.',
      { cateringPreparationDays, earliestOrderDate },
    ])
  }
  if (orderType === 'delivery') {
    notes.push([
      'Delivered from <strong>{{storeAddress}}</strong>.',
      { storeAddress },
    ])
    if (isDeliveredByOwner) {
      notes.push([
        'We need approximately <strong>{{byOwnerDeliveryTime}} mins</strong> to deliver your order.',
        { byOwnerDeliveryTime },
      ])

      if (byOwnerMaxDeliveryDistance > 0) {
        notes.push([
          'Only deliver within <strong>{{byOwnerMaxDeliveryDistance}} miles<strong>.',
          { byOwnerMaxDeliveryDistance },
        ])
      }
      if (byOwnerMinAmountToAcceptDelivery > 0) {
        notes.push([
          'Only deliver for order greater than <strong>{{amount}}</strong>.',
          {
            amount: formatCurrencyWithLocale(
              byOwnerMinAmountToAcceptDelivery,
              store?.locale ?? defaultLocale,
            ),
          },
        ])
      }
      if (byOwnerFreeDeliveryAmount > 0) {
        notes.push([
          'Free delivery for order greater than <strong>{{amount}}</strong>.',
          {
            amount: formatCurrencyWithLocale(
              byOwnerFreeDeliveryAmount,
              store?.locale ?? defaultLocale,
            ),
          },
        ])
      }
    }
  }

  return notes
}

export const acceptOrderFromTime = (
  store: Store,
  orderType?: OrderType,
  deliveryQuote?: DeliveryQuote,
) =>
  currentTzTime(store.timezone).add(
    getTotalDelayTimeInMinutes(store, orderType, deliveryQuote),
    'minutes',
  )

export const acceptOrderToTime = (store: Store) =>
  currentTzTime(store.timezone).add(30, 'days')

export const getPreparationTimeErrorMessage = (
  store: Store,
  orderType?: OrderType,
  deliveryQuote?: DeliveryQuote,
) => {
  let errorMessage = `We need at least ${getDelayTimeInMinutes(
    store,
    orderType,
  )} mins to prepare`
  if (orderType === 'delivery') {
    errorMessage += ` and ${getDeliveryTimeInMinutes(
      store,
      orderType,
      deliveryQuote,
    )} mins to delivery`
  }
  return `${errorMessage} your order`
}

export const canSetOrderDate = (
  store: Store,
  orderType: OrderType,
): boolean => {
  const cateringEnabled =
    !!store.online_order_catering_settings?.enable_online_catering
  const deliveredByOwner = store.delivery_settings?.delivery_type === 'owner'

  return cateringEnabled && (orderType !== 'delivery' || deliveredByOwner)
}

// Parse a minimum preparation time duration string into a number of days.
// The value uses a unit suffix so other units can be supported later,
// only days (e.g. "5d") are supported for now.
export const parsePreparationTimeToDays = (value?: string): number => {
  const match = /^(\d+)\s*d$/i.exec(value?.trim() ?? '')
  return match ? parseInt(match[1], 10) : 0
}

// The minimum number of preparation days required for the order, which is
// the largest minimum_preparation_time among all catering categories having
// at least one item in the order. Returns 0 when the order has no item
// belonging to a catering category.
export const getCateringPreparationDays = (
  order: Order | undefined,
  store: Store | undefined,
): number => {
  const items = order?.items ?? []
  const categories = store?.categories ?? []
  if (items.length === 0 || categories.length === 0) return 0

  const categoryIds = new Set(items.map((item) => item.categoryid))
  return categories.reduce(
    (days, category) =>
      category.catering === true && categoryIds.has(category.id)
        ? Math.max(
            days,
            parsePreparationTimeToDays(category.minimum_preparation_time),
          )
        : days,
    0,
  )
}

// The earliest date an order containing catering items can be fulfilled,
// which is today plus the required preparation days in the store timezone.
export const calculateCateringEarliestOrderDate = (
  cateringPreparationDays: number,
  tz?: string,
): moment.Moment =>
  currentTzTime(tz).add(cateringPreparationDays, 'days').startOf('day')

export const shouldGenerateTimeSlot = (
  currentTimeSlot: moment.Moment[],
  currentDate: moment.Moment,
): boolean => {
  return currentTimeSlot.length > 0
    ? !currentDate
        .startOf('day')
        .isSame(currentTimeSlot[0].clone().startOf('day'))
    : true
}

export const calculateAvailableTimeSlots = (
  baseTime: moment.Moment,
  delayTime: number,
  bizHours?: StoreBizHour[],
  tz?: string,
  holidays?: StoreHoliday[],
): moment.Moment[] => {
  const now = currentTzTime(tz)
  if (baseTime.isBefore(now, 'minute')) {
    return []
  }

  const period = 15
  const generateTimeSlot = (from?: string[], to?: string[]) => {
    const list = []

    let startBusinessTime = baseTime.clone()
    if (from) {
      startBusinessTime
        .hour(parseInt(from[0]))
        .minutes(parseInt(from[1]))
        .startOf('minute')
    }

    let endBusinessTime = baseTime.clone()
    if (to) {
      endBusinessTime
        .hour(parseInt(to[0]))
        .minutes(parseInt(to[1]))
        .startOf('minute')
    } else {
      endBusinessTime.endOf('day')
    }

    const baseTimeClone = baseTime.clone()
    let startTime = (
      baseTimeClone.isAfter(startBusinessTime)
        ? baseTimeClone
        : startBusinessTime.clone()
    )
      .clone()
      .add(delayTime, 'minutes')
      .startOf('minute')
    if (isToday(startTime.clone(), tz)) {
      const numberDivisible = startTime.clone().minute() % 5
      const remainder = numberDivisible === 0 ? 0 : 5 - numberDivisible
      startTime = startTime.add(5 + remainder, 'minute')
    }
    if (startTime.isAfter(endBusinessTime)) {
      return []
    }

    const endTime = endBusinessTime.clone().startOf('minute')

    if (isEmpty(endTime)) {
      return []
    }

    list.push(startTime.clone())

    while (startTime.isSameOrBefore(endTime)) {
      startTime.add(period, 'minutes')
      list.push(startTime.clone())
    }

    list.splice(-1)

    if (isToday(startTime, tz)) {
      return list.filter((e) => e.clone().isSame(currentTzTime(tz), 'date'))
    }

    return list
  }

  // A holiday, when configured for this day, overrides the regular biz
  // hours entirely — matching isStoreOpening's precedence in TimeService.
  const [, holiday] = isInWorkingHoursHoliday(baseTime, holidays, tz)
  const currentBizHours =
    holiday ?? bizHours?.find((e) => e.day_of_week === baseTime.weekday())

  if (currentBizHours) {
    if (!currentBizHours.is_working) {
      return []
    }

    const timeSlotPairs = [
      {
        from: currentBizHours.from_1,
        to: currentBizHours.to_1,
      },
      {
        from: currentBizHours.from_2,
        to: currentBizHours.to_2,
      },
    ].filter((e) => e.from && e.to)

    return timeSlotPairs
      .map((e) => generateTimeSlot(e.from?.split(':'), e.to?.split(':')))
      .reduce((a, b) => a.concat(b), [])
  }

  return generateTimeSlot()
}

export const getStartOfDay = (
  currentTime: moment.Moment,
  tz?: string,
): moment.Moment => {
  if (isToday(currentTime, tz)) {
    return toTzTime(undefined, tz)
  }
  return currentTime.startOf('day')
}

export const calculateAcceptingOrder = (
  currentStore: Store | undefined,
): {
  isAcceptingOrder: boolean
  pauseOrderBefore?: moment.Moment
} => {
  if (!currentStore || currentStore.accept_order === false) {
    return { isAcceptingOrder: false }
  }
  if (isEmpty(currentStore.pause_online_ordering_before)) {
    return { isAcceptingOrder: true }
  }

  const pauseOrderBefore = toTzTime(
    currentStore.pause_online_ordering_before,
    currentStore.timezone,
  )
  const currentTime = currentTzTime(currentStore.timezone)
  const isBefore = currentTime.isBefore(pauseOrderBefore)
  return { isAcceptingOrder: !isBefore, pauseOrderBefore }
}

export const checkCanSubmitOrder = (
  isAcceptingOrder: boolean,
  order: Order | undefined,
): boolean => {
  return isAcceptingOrder === true && order !== undefined && order.subtotal > 0
}

export const checkIsAsapEnabled = (
  isOpening: boolean,
  store: Store | undefined,
  orderType: OrderType | undefined,
): boolean => {
  if (!store || !orderType || !isOpening) {
    return false
  }

  const delayTime = getDelayTimeInMinutes(store, orderType)
  const pickupTime = currentTzTime(store.timezone).add(delayTime, 'minutes')
  const [isWorking] = isInBizHour(pickupTime, store.biz_hours, store.timezone)

  return isWorking
}

export const calculateCategorySchedules = (
  store: Store,
  category: MenuCategory | undefined,
  order?: Order,
): string => {
  if (category === undefined) return ''

  const schedules = category?.schedules
  const unavailableMessage =
    category?.schedules?.unavailable_message ??
    'Sorry! This item is unavailable right now'

  if (schedules?.always_available === false) {
    let date = currentTzTime(store.timezone)
    if (
      order !== undefined &&
      order.is_asap === false &&
      order.time !== undefined
    ) {
      date = parseTzTime(order.time, timestampFormat, store.timezone, true)
      if (order.order_type === 'delivery') {
        const totalDelayTime = getTotalDelayTimeInMinutes(
          store,
          order.order_type,
          order.delivery_quote,
        )
        date = date.add(-totalDelayTime, 'minutes')
      }
    }

    const availableHour = schedules?.available_hours?.find(
      (h) => h.day_of_week === date.weekday(),
    )

    if (availableHour?.is_available) {
      const currentDateObject = {
        year: date.get('year'),
        month: date.get('month'),
        date: date.get('date'),
      }

      const haveTime1 =
        !isEmpty(availableHour.from_1) && !isEmpty(availableHour.to_1)
      let betweenTime1 = false
      if (haveTime1) {
        const from1 = parseTzTime(
          availableHour.from_1,
          'HH:mm',
          store.timezone,
          true,
        )
        from1.set(currentDateObject)
        const to1 = parseTzTime(
          availableHour.to_1,
          'HH:mm',
          store.timezone,
          true,
        )
        to1.set(currentDateObject)
        betweenTime1 = date.isBetween(from1, to1, 'minute', '[]')
      }

      const haveTime2 =
        !isEmpty(availableHour.from_2) && !isEmpty(availableHour.to_2)
      let betweenTime2 = false
      if (haveTime2) {
        const from2 = parseTzTime(
          availableHour.from_2,
          'HH:mm',
          store.timezone,
          true,
        )
        from2.set(currentDateObject)
        const to2 = parseTzTime(
          availableHour.to_2,
          'HH:mm',
          store.timezone,
          true,
        )
        to2.set(currentDateObject)
        betweenTime2 = date.isBetween(from2, to2, 'minute', '[]')
      }

      if (
        (haveTime1 || haveTime2) &&
        (!haveTime1 || !betweenTime1) &&
        (!haveTime2 || !betweenTime2)
      ) {
        return unavailableMessage
      }
    } else {
      return unavailableMessage
    }
  }

  return ''
}

export const checkOrderByTimeChange = (
  store: Store,
  order: Order,
): [boolean, Order?] => {
  const itemCount = order.items.length
  const items = [...order.items]
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const category = store.categories?.find((cat) => cat.id === item.categoryid)
    const isAvailable =
      calculateCategorySchedules(store, category, order) === ''

    if (!isAvailable) {
      items.splice(i, 1)
      i--
    }
  }

  if (items.length !== itemCount) {
    const newOrder = updateOrderAmounts({ ...order, items }, store)
    return [true, newOrder]
  }

  return [false]
}

export const checkCanShowEditItemDialog = (
  item: MenuItem,
  store: Store,
): boolean => {
  const modifiers = item.modifiers?.filter((m) =>
    store.modifiers?.find(
      (sm) => sm.id === m.id && sm.online_ordering !== false,
    ),
  )
  return (
    !isEmpty(item.description) ||
    !isEmpty(modifiers) ||
    store.show_special_note === true
  )
}

export const addNonEditableItemToOrder = (
  item: MenuItem,
  category: MenuCategory,
  order: Order,
): Order => {
  const existingIndex = order.items.findIndex((oi) => oi.itemid === item.id)
  const oItems = [...order.items]
  if (existingIndex >= 0) {
    const updatedItem = {
      ...oItems[existingIndex],
      count: oItems[existingIndex].count + 1,
    }
    oItems[existingIndex] = updatedItem
  } else {
    const orderItem = newOrderItem(category, item)
    oItems.push(orderItem)
  }

  return { ...order, items: oItems }
}

export const checkIsSplitTip = (store?: Store, orderType?: string): boolean => {
  if (!store || !orderType) return false

  const thirdParties = ['doordash']
  const splitTipSetting = store.tip_settings?.split_tip_for_restaurant_staff
  const deliveryTypeSetting = store.delivery_settings?.delivery_type ?? ''

  return (
    splitTipSetting === true &&
    thirdParties.includes(deliveryTypeSetting) &&
    orderType === 'delivery'
  )
}

export const calculateOrderTime = (
  store: Store,
  order: Order,
): moment.Moment => {
  const orderTime = currentTzTime(store.timezone).add(
    getTotalDelayTimeInMinutes(store, order.order_type, order.delivery_quote),
    'minutes',
  )

  return orderTime
}

export const calculateOutOfStockItem = (item: MenuItem): string => {
  if (
    item === undefined ||
    isEmpty(item.out_of_stock) ||
    item.out_of_stock === 'In-stock'
  ) {
    return ''
  }

  return `Out of stock ${item.out_of_stock}`
}

export const calculateUnavailableMessageWhenSearching = (
  item: MenuItem,
  store: Store,
  order?: Order,
) => {
  const category = store.categories?.find((cat) =>
    cat.items?.some((i) => i.id === item.id),
  )

  const categoryUnavailableMessage = category
    ? calculateCategorySchedules(store, category, order)
    : ''

  return categoryUnavailableMessage
}

export const calculateUnavailableMessageInMenu = (
  item: MenuItem,
  unavailableMessage?: string,
  calculateUnavailableMessageWhenSearching?: (item: MenuItem) => any,
) => {
  // Display out of stock message if item is out of stock
  const outOfStockMessage = calculateOutOfStockItem(item)
  if (outOfStockMessage) {
    return outOfStockMessage
  }

  // Display unavailable message if there is an unavailable message (e.g. from category schedules)
  if (unavailableMessage) {
    return unavailableMessage
  }

  if (!calculateUnavailableMessageWhenSearching) {
    return ''
  }

  return calculateUnavailableMessageWhenSearching(item) || ''
}

export const searchItemInMenu = (
  store: Store,
  searchText: string,
): MenuItem[] => {
  // Normalize the search value:
  // 1. Trim extra whitespace
  // 2. Convert to lowercase
  // 3. Remove special characters (keeping letters and numbers)
  const normalizedSearch = searchText
    .toLowerCase()
    .replace(/[^a-zA-Z0-9\sÀ-ÿ]/g, '') // This pattern keeps basic Latin letters, numbers, spaces and common accented characters
    .trim()

  if (!normalizedSearch) {
    return store.categories?.flatMap((x) => x.items ?? []) || []
  }

  // Search through all categories and their items
  const results: MenuItem[] = []
  store.categories?.forEach((category) => {
    category.items?.forEach((item) => {
      if (!item.name) return

      // Get the full name by combining item.name and item.name2 (if it exists)
      const fullName = item.name2 ? `${item.name} ${item.name2}` : item.name

      // Normalize the full name the same way
      const normalizedFullName = fullName
        .toLowerCase()
        .replace(/[^a-zA-Z0-9\sÀ-ÿ]/g, '')

      // Check if the normalized full name includes the normalized search value
      if (normalizedFullName.includes(normalizedSearch)) {
        results.push(item)
      }
    })
  })

  return results
}

export const calculateItemPriceWhenChangeOrderType = (
  order: Order,
  store: Store,
  newOrderType: OrderType,
): Order => {
  const updatedOrder = { ...order, order_type: newOrderType }

  updatedOrder.items = updatedOrder.items.map((item) =>
    updateOrderItemSubtotal(item, newOrderType, store),
  )

  const finalOrder = updateOrderAmounts(updatedOrder, store)
  return finalOrder
}

export const getAdjustedOrderItemPrice = (
  price: number | undefined,
  orderType: OrderType,
  store: Store,
): number => {
  if (price === undefined || price <= 0) {
    return 0
  }

  // If the store has delivery surcharge, apply it to the price
  if (
    orderType === 'delivery' &&
    store.delivery_settings?.delivery_surcharge_percent
  ) {
    return roundM(
      price * (1 + store.delivery_settings.delivery_surcharge_percent),
    )
  }
  return price
}

export const calculateEarliestAvailableTime = (
  store: Store,
): moment.Moment | undefined => {
  if (!store || !store.biz_hours) {
    return undefined
  }

  const currentDate = currentTzTime(store.timezone)
  const [isOpening] = isStoreOpening(store, currentDate)

  // If store is open, return current time
  if (isOpening) {
    return currentDate
  }

  // If store is closed, calculate the next opening time
  const dayOfWeek = currentDate.weekday()

  // Helper to create a moment for a given day and time string (e.g., '09:00')
  const createOpeningMoment = (baseDate: moment.Moment, timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return baseDate.clone().startOf('day').set({ hours, minutes })
  }

  // Check the next 7 days for the earliest opening time
  for (let i = 0; i < 7; i++) {
    const checkDate = currentDate.clone().add(i, 'days')
    const checkDayOfWeek = (dayOfWeek + i) % 7

    // A holiday, when configured for this day, overrides the regular biz
    // hours entirely — matching isStoreOpening's precedence in TimeService.
    const [, holiday] = isInWorkingHoursHoliday(
      checkDate,
      store.holidays,
      store.timezone,
    )
    const bizHour =
      holiday ?? store.biz_hours.find((h) => h.day_of_week === checkDayOfWeek)

    if (bizHour?.is_working) {
      // Check first opening period if exists
      if (bizHour.from_1) {
        const openingDate = createOpeningMoment(checkDate, bizHour.from_1)
        if (openingDate.isAfter(currentDate)) {
          return openingDate
        }
      }
      // Check second opening period if exists
      if (bizHour.from_2) {
        const secondOpeningDate = createOpeningMoment(checkDate, bizHour.from_2)
        if (secondOpeningDate.isAfter(currentDate)) {
          return secondOpeningDate
        }
      }
    }
  }

  return undefined
}
